/**
 * O clima de cada mapa (modo_historia_mundos/07#HM-001). ADR 0062. Logica pura: as particulas nascem de uma
 * semente tirada do `mapId` -- todos os clientes veem o mesmo ceu sem uma mensagem de rede -- e andam com o
 * tempo. A cena so as desenha.
 *
 * O tipo de clima e dado do mapa (a propriedade `ambient`), como no IDLE FORCE, onde ele vem do bioma.
 */

export const AMBIENT_KINDS = ['fireflies', 'embers', 'sand', 'sparks', 'petals', 'wisps', 'clouds', 'leaves'] as const
export type AmbientKind = (typeof AMBIENT_KINDS)[number]

export const isAmbientKind = (value: unknown): value is AmbientKind =>
  typeof value === 'string' && (AMBIENT_KINDS as readonly string[]).includes(value)

/**
 * Como cada clima se comporta. `density` e quantas particulas por 100 tiles; `speed`, em tiles por segundo, e o
 * vento (x) e a subida (y, negativa sobe); `glow` desenha com mistura aditiva, a luz do IDLE FORCE.
 */
export type AmbientSpec = {
  density: number
  color: number
  /** Raio, em fracao do tile. */
  size: number
  speed: { x: number; y: number }
  /** Amplitude do balanco lateral, em tiles. */
  sway: number
  /** Quanto o brilho pisca (0 a 1). */
  flicker: number
  glow: boolean
}

export const AMBIENT: Readonly<Record<AmbientKind, AmbientSpec>> = {
  fireflies: {
    density: 3,
    color: 0xd9ff7a,
    size: 0.09,
    speed: { x: 0.05, y: -0.03 },
    sway: 0.6,
    flicker: 0.9,
    glow: true,
  },
  embers: { density: 4, color: 0xff8a3d, size: 0.07, speed: { x: 0.1, y: -0.5 }, sway: 0.3, flicker: 0.6, glow: true },
  sand: { density: 5, color: 0xe8d29a, size: 0.05, speed: { x: 1.2, y: 0.05 }, sway: 0.15, flicker: 0.2, glow: false },
  sparks: { density: 2, color: 0x9fd8ff, size: 0.06, speed: { x: 0, y: 0.2 }, sway: 0.4, flicker: 1, glow: true },
  petals: { density: 3, color: 0xffb7d5, size: 0.08, speed: { x: 0.4, y: 0.25 }, sway: 0.8, flicker: 0, glow: false },
  wisps: { density: 1.5, color: 0xb98cff, size: 0.14, speed: { x: 0.08, y: -0.08 }, sway: 1, flicker: 0.7, glow: true },
  clouds: { density: 0.15, color: 0x000000, size: 4, speed: { x: 0.35, y: 0.08 }, sway: 0, flicker: 0, glow: false },
  leaves: { density: 2, color: 0x8fc25a, size: 0.08, speed: { x: 0.35, y: 0.3 }, sway: 0.9, flicker: 0, glow: false },
}

export type Particle = { x: number; y: number; phase: number }

/** FNV-1a de 32 bits: a semente do mapa, a mesma em toda maquina. */
export function seedOf(text: string): number {
  let hash = 0x811c9dc5
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193)
  }
  return hash >>> 0
}

/** Mulberry32: um gerador pequeno e deterministico, so para o clima -- nada de regra passa por aqui. */
export function random(seed: number): () => number {
  let state = seed
  return () => {
    state = (state + 0x6d2b79f5) | 0
    let mixed = Math.imul(state ^ (state >>> 15), 1 | state)
    mixed = (mixed + Math.imul(mixed ^ (mixed >>> 7), 61 | mixed)) ^ mixed
    return ((mixed ^ (mixed >>> 14)) >>> 0) / 4294967296
  }
}

/** As particulas de um mapa, em tiles, espalhadas pela semente dele. */
export function spawnParticles(mapId: string, kind: AmbientKind, width: number, height: number): Particle[] {
  const next = random(seedOf(`${mapId}:${kind}`))
  const count = Math.max(1, Math.round((AMBIENT[kind].density * width * height) / 100))
  return Array.from({ length: count }, () => ({ x: next() * width, y: next() * height, phase: next() * Math.PI * 2 }))
}

/** Onde a particula esta no instante `seconds`: o vento, o balanco e a volta pela borda do mapa. */
export function particleAt(
  particle: Particle,
  spec: AmbientSpec,
  seconds: number,
  width: number,
  height: number,
): { x: number; y: number; alpha: number } {
  const wrap = (value: number, size: number): number => ((value % size) + size) % size
  const x = wrap(particle.x + spec.speed.x * seconds + spec.sway * Math.sin(seconds * 0.8 + particle.phase), width)
  const y = wrap(particle.y + spec.speed.y * seconds, height)
  const alpha = 1 - spec.flicker * (0.5 + 0.5 * Math.sin(seconds * 2.3 + particle.phase * 3))
  return { x, y, alpha }
}
